import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors, } from "@dnd-kit/core";
import { faArrowDownLeftAndArrowUpRightToCenter, faArrowUpRightAndArrowDownLeftFromCenter, faBorderAll, faCalendars, faTableColumns, } from "@fortawesome/pro-regular-svg-icons";
import { faShop, faSpinnerScale } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Form, message, Modal, Radio, Row, Select, Typography } from "antd";
import { addDays, format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Split from "react-split";
import CustomDateTimePicker from "../../common/CustomDateTimePicker";
import { useAuth } from "../../config/AuthProvider";
import { ContainerDeliveryMethodSelect } from "../../helpers/common";
import dayjs from "../../helpers/dayjsSetup";
import { useGlobalFilters } from "../../providers/GlobalFiltersProvider";
import { queryClient } from "../../react-query-client";
import ContainerService from "../../services/Container/ContainerService";
import OrgService from "../../services/Org/OrgService";
import CalendarDashboard from "../CalendarDashboard/CalendarDashboard";
import WarehouseFilter from "../Filters/WarehouseFilter";
import GridDashboard from "../GridDashboard/GridDashboard";
import { InvalidateDashboardQueries } from "./DashboardCacheCleaner";
import KanbanColumn from "./KanbanColumn";
import VesselCard from "./VesselCard";
import WarehouseDetailSelection from "./WarehouseDetailSelection";
import AppButton from "../../common/AppButton";

const { Title } = Typography;

const getISODate = (date) => format(date, "yyyy-MM-dd");
const getDayLabel = (date) => format(date, "EEEE");
const getShortDate = (date) => format(date, "dd MMM");

const KanbanBoard = ({ warehouses }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [arrivingDate, setArrivingDate] = useState(null);
  const [unloadingDate, setUnloadingDate] = useState(null);
  const [fullscreenSection, setFullscreenSection] = useState(location?.state?.fullscreenSection || null);
  const [activeVessel, setActiveVessel] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [pendingMoveData, setPendingMoveData] = useState(null);
  const isMobile = window.innerWidth < 768;
  const [activeView, setActiveView] = useState(isMobile ? "grid" : (location?.state?.activeView ? location.state.activeView : "table"));
  const [deliveryForm] = Form.useForm();
  const { filters } = useGlobalFilters();
  const isTableView = activeView === "table";
  const isCalendarView = activeView === "calendar";
  const isGridView = activeView === "grid";
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5, }, }),
    useSensor(KeyboardSensor)
  );

  const { data: initialDates, isFetched, isError } = useQuery({
    queryKey: ["initial-dates", filters],
    queryFn: () => ContainerService.getInitialDates({ ...filters }),
    enabled: isTableView
  });

  useEffect(() => {
    if (location?.state?.arrivingDate && location?.state?.unloadingDate) {
      setArrivingDate(new Date(location.state.arrivingDate));
      setUnloadingDate(new Date(location.state.unloadingDate));
      return;
    }

    if (initialDates) {
      setArrivingDate(new Date(initialDates.arrivingDate));
      setUnloadingDate(new Date(initialDates.unloadingDate));
      return;
    }

    if (isFetched && isError) {
      const today = new Date();
      setArrivingDate(today);
      setUnloadingDate(today);
    }
  }, [initialDates, isFetched, isError, location?.state?.arrivingDate, location?.state?.unloadingDate]);

  const arrivingISO = arrivingDate ? getISODate(arrivingDate) : null;
  const unloadingISO = unloadingDate ? getISODate(unloadingDate) : null;

  const { data: arrivingColumns = {} } = useQuery({
    queryKey: ["arriving", arrivingISO, fullscreenSection, filters],
    queryFn: () => ContainerService.loadArrivingData({ date: arrivingISO, fullscreenSection, ...filters }),
    enabled: isTableView && !!arrivingISO
  });

  const { data: unloadingColumns = {} } = useQuery({
    queryKey: ["unloading", unloadingISO, fullscreenSection, filters],
    queryFn: () => ContainerService.loadUnloadingData({ date: unloadingISO, fullscreenSection, ...filters }),
    enabled: isTableView && !!unloadingISO
  });

  const { data: emptiedColumns = {} } = useQuery({
    queryKey: ["emptied", filters],
    queryFn: () => ContainerService.loadEmptiedData({ ...filters }),
    enabled: isTableView
  });

  const { data: arrivingPrevNextCount = {} } = useQuery({
    queryKey: ["arriving-prev-next", arrivingISO, filters],
    queryFn: () => ContainerService.getArrivingPrevNextCount({ date: arrivingISO, ...filters }),
    enabled: isTableView && !!arrivingISO
  });

  const { data: unloadingPrevNextCount = {} } = useQuery({
    queryKey: ["unloading-prev-next", unloadingISO, filters],
    queryFn: () => ContainerService.getUnloadingPrevNextCount({ date: unloadingISO, ...filters }),
    enabled: isTableView && !!unloadingISO
  });

  const moveMutation = useMutation({
    mutationFn: ({
      containerID,
      oldSection,
      newSection,
      newETA,
      deliveryMethod,
      deliveryDate,
      warehouseDetail,
    }) => ContainerService.updateDashboardContainer(containerID, oldSection, newSection, newETA, deliveryMethod, deliveryDate, warehouseDetail),
    onSuccess: () => {
      InvalidateDashboardQueries(queryClient);
    },
    onError: () => {
      message.error("Failed to move container");
    },
  });

  const completeVesselMove = async ({
    sourceColumnId,
    destinationColumnId,
    movedItem,
    sourceType,
    destinationType,
  }) => {
    if (sourceType !== destinationType || sourceColumnId !== destinationColumnId) {
      const oldSection = sourceType.toUpperCase();
      const newSection = destinationType.toUpperCase();
      const containerID = movedItem.id;
      const newETA = newSection !== "EMPTIED" ? new Date(destinationColumnId.split("date-")[1]) : null;
      const { deliveryMethod, deliveryTime } = deliveryForm.getFieldsValue();
      const deliveryDate = oldSection === "ARRIVING" && newSection === "UNLOADING" ? `${dayjs(newETA).format("YYYY-MM-DD")}T${deliveryTime.format("HH:mm")}` : null;
      const { warehouseID, warehouseReceivingZoneID, warehouseDockID } = deliveryForm.getFieldsValue();
      const warehouseDetail = { warehouseID, warehouseReceivingZoneID, warehouseDockID };
      deliveryForm.resetFields();
      try {
        await moveMutation.mutateAsync({
          containerID,
          oldSection,
          newSection,
          newETA: newSection !== "EMPTIED" ? newETA.toISOString() : null,
          deliveryMethod: newSection === "UNLOADING" ? deliveryMethod : null,
          deliveryDate,
          warehouseDetail
        });
        message.success(`Moved Container #${movedItem?.containerNum} to ${newSection}`);
      } catch (err) {
        message.error(`Failed to move Container #${movedItem?.containerNum}`);
        console.error(err);
      }
    }
  };

  const onDragStart = ({ active }) => {
    const scrollEls = document.querySelectorAll(".kanban-scroll-container");
    scrollEls.forEach((el) => el.classList.add("no-horizontal-drag"));
    setActiveVessel(active.data?.current?.vessel || null);
  };

  const onDragEnd = async ({ active, over }) => {
    const scrollEls = document.querySelectorAll(".kanban-scroll-container");
    scrollEls.forEach((el) => el.classList.remove("no-horizontal-drag"));
    setActiveVessel(null);
    if (!over || active.id === over.id) return;

    let sourceColumnId = active.data?.current?.columnId;
    if (sourceColumnId.includes("emptied")) {
      sourceColumnId = sourceColumnId.replace("all", active.data?.current?.vessel?.etaDate);
    }
    const destinationColumnId = over.data?.current?.columnId;

    if (!sourceColumnId || !destinationColumnId) return;

    const sourceType = sourceColumnId.split("-")[0];
    const destinationType = destinationColumnId.split("-")[0];

    const isAllowedMove = () => {
      if (sourceType === "arriving" && destinationType === "unloading") {
        setPendingMoveData({
          sourceColumnId,
          destinationColumnId,
          movedItem: active.data?.current?.vessel,
          sourceType,
          destinationType,
        });
        setShowDeliveryModal(true);
        return false;
      }

      if (sourceType === "arriving" && destinationType === "emptied") return true;

      if (sourceType === "unloading" && destinationType !== "emptied" && active.data?.current?.vessel?.isDeliveryConfirmed) {
        message.destroy();
        message.error(
          <>
            Container delivery has already been confirmed and can not be rescheduled.
            <br />
            To enable rescheduling, please update 'Delivery Confirmed' to 'No'.
          </>,
          10
        );
        return false;
      }

      if (sourceType === "unloading" && destinationType === "emptied") return true;
      if (sourceType === "unloading" && destinationType === "arriving") return true;
      if (sourceType === "emptied" && destinationType === "arriving") return true;
      if (sourceType === "emptied" && destinationType === "unloading") return true;
      if (sourceType === destinationType && sourceColumnId !== destinationColumnId) return true;

      return false;
    };

    if (!isAllowedMove()) {
      return;
    }

    await completeVesselMove({
      sourceType,
      destinationType,
      movedItem: active.data?.current?.vessel,
      destinationColumnId,
    });
  };

  const onDragCancel = () => {
    const scrollEls = document.querySelectorAll(".kanban-scroll-container");
    scrollEls.forEach((el) => el.classList.remove("no-horizontal-drag"));
    setActiveVessel(null);
  };

  const onVesselClick = (vessel) => {
    navigate("/shipment-gateway/shipment-details", {
      state: {
        pathname: location.pathname,
        containerID: vessel?.id,
        arrivingDate,
        unloadingDate,
        fullscreenSection,
        activeView,
      },
    });
  };

  const generateColumns = useCallback(
    (baseDate, columns, type) => {
      return Array.from({
        length: fullscreenSection === "arriving" ? 10 : (fullscreenSection === "unloading" ? 9 : 7),
      }).map((_, idx) => {
        const day = addDays(baseDate, idx);
        const key = `${type}-date-${getISODate(day)}`;
        return (
          <KanbanColumn
            key={key}
            id={key}
            title={getShortDate(day)}
            subtitle={getDayLabel(day)}
            vessels={columns[key] || []}
            isHoliday={day.getDay() === 0 || day.getDay() === 6}
            onVesselClick={onVesselClick}
          />
        );
      });
    },
    [onVesselClick]
  );

  const renderSection = (label, type, date, setDate, columns) => (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        background: "white",
      }}
      className={`${fullscreenSection === "arriving"
        ? "rounded-t-3xl"
        : fullscreenSection === "unloading"
          ? "rounded-ss-3xl"
          : type === "arriving"
            ? "rounded-ss-3xl"
            : "rounded-se-3xl"
        }`}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Title className="mt-5 !mb-0 ml-5 !font-medium font-inter !text-[22px] text-[#1E1E1E]">
          {label}
        </Title>
        <div className="mt-5 !mb-0 ml-5">
          <CustomDateTimePicker
            type="date"
            value={dayjs(date)}
            onChange={(d) => setDate(d ? d.toDate() : new Date())}
            className="h-10 rounded-lg"
          />
          <Button
            className="h-10 bg-[#F7F6F8] font-bold ml-2 align-middle"
            onClick={() => setDate(addDays(date, -1))}
          >
            <span className="px-2 rounded-md bg-[#01A4A6] font-semibold text-white">
              {type === "arriving" ? arrivingPrevNextCount.arrivingPrevCount : unloadingPrevNextCount.unloadingPrevCount}
            </span>
            <LeftOutlined />
          </Button>
          <Button
            className="h-10 bg-[#F7F6F8] font-bold ml-2 align-middle"
            onClick={() => setDate(addDays(date, 1))}
          >
            <RightOutlined />
            <span className="px-2 rounded-md bg-[#01A4A6] font-semibold text-white">
              {type === "arriving"
                ? arrivingPrevNextCount.arrivingNextCount
                : unloadingPrevNextCount.unloadingNextCount}
            </span>
          </Button>
          <Button
            type="text"
            className="h-10 font-bold mx-2 align-middle"
            icon={
              fullscreenSection === type ? (
                <FontAwesomeIcon
                  icon={faArrowDownLeftAndArrowUpRightToCenter}
                  className="text-xl text-[#545454]"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faArrowUpRightAndArrowDownLeftFromCenter}
                  className="text-xl text-[#545454]"
                />
              )
            }
            onClick={() =>
              setFullscreenSection(fullscreenSection === type ? null : type)
            }
          />
        </div>
      </Row>
      <div
        className="kanban-scroll-container"
        style={{ display: "flex", flex: 1, overflowX: "auto" }}
      >
        {generateColumns(date, columns, type)}
      </div>
    </div>
  );

  const renderEmptiedSection = () => {
    const allVessels = Object.values(emptiedColumns).flat();
    const id = "emptied-date-all";
    return (
      <div
        style={{
          display: "flex",
          flex: "0 0 auto",
          flexDirection: "column",
          width: 152,
        }}
      >
        <KanbanColumn
          id={id}
          title="Emptied"
          subtitle=""
          vessels={allVessels}
          isHoliday={false}
          onVesselClick={onVesselClick}
        />
      </div>
    );
  };
  const [suppliers, setSuppliers] = useState([]);

  const fetchSuppliers = () => {
    if (user?.isSupplier) return;
    OrgService.getSupplierDropDown()
      .then((response) => {
        if (response.status === 200) {
          const sortedSuppliers = response.data.sort((a, b) =>
            a.supplierShortCode.localeCompare(b.supplierShortCode)
          );
          setSuppliers(sortedSuppliers);
        }
      })
      .catch((error) => {
        console.error("Error fetching supplier:", error);
      });
  };

  const [selectedSupplier, setSelectedSupplier] = useState(
    location?.state?.filterSupplier || null
  );
  const [selectedFilterStatus, setSelectedFilterStatus] = useState(
    location?.state?.filterStatus || "all"
  );

  const handleSupplierSelect = (supplierShortCode) => {
    setSelectedSupplier(supplierShortCode);
  };

  useEffect(() => {
    if ((isCalendarView || isGridView) && suppliers.length === 0) {
      fetchSuppliers();
    }
  }, [activeView])
  
  const handleClose = () => {
    setShowDeliveryModal(false);
    deliveryForm.resetFields();
    setPendingMoveData(null);
  }

  const handleSubmit = async () => {
    try {
      await deliveryForm.validateFields();
      const {
        sourceList,
        destinationList,
        sourceColumnId,
        destinationColumnId,
        movedItem,
        insertAt,
        sourceType,
        destinationType,
      } = pendingMoveData;
      setShowDeliveryModal(false);
      setPendingMoveData(null);
      await completeVesselMove({
        sourceList,
        destinationList,
        sourceColumnId,
        destinationColumnId,
        movedItem,
        insertAt,
        sourceType,
        destinationType,
      });
    } catch (error) {
      console.error("Failed to confirm delivery:", error);
    }
    }

    useEffect(() => {
        if (location?.state?.activeView === activeView) return;
        navigate(location.pathname, {
            replace: true,
            state: {
                ...location.state,
                activeView,
            },
        });
    }, [activeView, location.pathname]);

  return (
    <div className="pt-3 px-8 h-[100%]">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-2 mb-1 mx-1 pl-2 space-y-1 md:space-y-0">
        {/* Left Section */}
        <div className="flex flex-row items-center w-full space-x-1">
          <span className="font-inter !font-medium text-base md:text-[34px] !text-[#015373]">
            Delivery Planner
          </span>
          <Radio.Group
            value={activeView}
            onChange={(e) => setActiveView(e.target.value)}
            className="flex"
          >
            {[
              { value: "table", icon: faTableColumns },
              { value: "calendar", icon: faCalendars },
              { value: "grid", icon: faBorderAll },
            ].map(({ value, icon }) => (
              <Radio.Button
                key={value}
                value={value}
                className={`w-8 h-8 flex justify-center items-center ${activeView === value
                  ? "!bg-[#04C6BD] !border-[#04C6BD]"
                  : "!bg-white !border-[#d9d9d9]"
                  }`}
              >
                <FontAwesomeIcon
                  icon={icon}
                  className={`text-sm ${activeView === value ? "text-white" : "text-[#545454]"
                    }`}
                />
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
        <div>
          <div className="flex items-center font-inter">
            <WarehouseFilter warehouses={warehouses} />
            {isTableView ? null : (user?.isSupplier ? null :
              <>
                <Select
                  value={selectedSupplier}
                  prefix={<FontAwesomeIcon icon={faShop} className="text-gray-500 mr-1" />}
                  className='!h-[34px] w-48 ml-2'
                  showSearch
                  placeholder="Select Supplier"
                  allowClear
                  onClear={() => setSelectedSupplier(null)}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                  // onChange={handleSupplierChange}
                  onSelect={handleSupplierSelect}
                >
                  {suppliers.map((c) => (
                    <Select.Option key={c.id} value={c.supplierShortCode} label={c.supplierShortCode}>
                      {c.supplierShortCode}
                    </Select.Option>
                  ))}
                </Select>
                <Select
                  value={selectedFilterStatus}
                  options={[
                    { label: 'All', value: 'all' },
                    { label: 'In Transit', value: 'inTransit' },
                    { label: 'Arriving', value: 'arriving' },
                    { label: 'Delivery Scheduled', value: 'deliveryScheduled' },
                  ]}
                  className="w-48 ml-2" placeholder="Filter by status" onSelect={(value) => setSelectedFilterStatus(value)}
                  prefix={<FontAwesomeIcon icon={faSpinnerScale}
                    className="text-gray-500 mr-2"
                  />}
                  onClear={() => setSelectedFilterStatus("all")}
                  allowClear={true} />
              </>
            )}
          </div>
        </div>
      </div>

      {isTableView && (<>
        <DndContext
          sensors={sensors}
          autoScroll={false}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          {fullscreenSection ? (
            <div style={{ display: "flex", width: "100%" }}>
              {renderSection(
                fullscreenSection === "arriving"
                  ? "ARRIVING AT POD"
                  : "DELIVERY SCHEDULE",
                fullscreenSection,
                fullscreenSection === "arriving" ? arrivingDate : unloadingDate,
                fullscreenSection === "arriving"
                  ? setArrivingDate
                  : setUnloadingDate,
                fullscreenSection === "arriving"
                  ? arrivingColumns
                  : unloadingColumns
              )}
              {fullscreenSection === "unloading" && renderEmptiedSection()}
            </div>
          ) : (
            <Split
              sizes={[50, 50]}
              minSize={[300, 300]}
              gutterSize={4}
              expandToMin
              direction="horizontal"
              style={{ display: "flex", height: "100%" }}
              gutter={(index, direction) => {
                const gutter = document.createElement("div");
                gutter.className = `custom-gutter custom-gutter-${direction}`;
                return gutter;
              }}
            >
              {renderSection(
                "ARRIVING AT POD",
                "arriving",
                arrivingDate,
                setArrivingDate,
                arrivingColumns
              )}
              {renderSection(
                "DELIVERY SCHEDULE",
                "unloading",
                unloadingDate,
                setUnloadingDate,
                unloadingColumns
              )}
            </Split>
          )}
          <DragOverlay>
            {activeVessel ? (
              <VesselCard vessel={activeVessel} isDragging={true} />
            ) : null}
          </DragOverlay>
        </DndContext>
        <Modal
          width={300}
          title="Add Delivery Details"
          open={showDeliveryModal}
          onCancel={handleClose}
          maskClosable={false}
          footer={null}
        >
          <Form layout="vertical" form={deliveryForm}>
            <Form.Item
              name="deliveryTime"
              label="Delivery Time"
              rules={[{ required: true, message: "Please select delivery time" }]}
            >
              <CustomDateTimePicker
                type="time"
                form={deliveryForm}
                itemName="deliveryTime"
                className="w-full"
              />
            </Form.Item>
            <Form.Item
              name="deliveryMethod"
              label="Delivery Method"
              rules={[
                { required: true, message: "Please select delivery method" },
              ]}
            >
              <ContainerDeliveryMethodSelect placeholder="Select delivery method" />
            </Form.Item>
            <WarehouseDetailSelection warehouseDetail={pendingMoveData?.movedItem?.warehouseDetail}
                                      form={deliveryForm}
                                      key={pendingMoveData?.movedItem?.id} />
            <div className="flex justify-end">
            <AppButton variant="save" onClick={handleSubmit}>
              Save
            </AppButton>
            <AppButton variant="close" onClick={handleClose}>
              Close
            </AppButton>
            </div>
          </Form>
        </Modal> </>)}

      {isCalendarView && (
        <CalendarDashboard
          date={arrivingDate}
          filterStatus={selectedFilterStatus}
          filterSupplier={selectedSupplier}
        />
      )}

      {isGridView && (
        <GridDashboard
          filterStatus={selectedFilterStatus}
          filterSupplier={selectedSupplier}
        />
      )}
    </div>
  );
};

export default KanbanBoard;